import { useState, useEffect } from "react";
import { ButtonSiguiente, Input, Select } from "../../ui";
import { safeFetch } from "@/api/safeFetch";
import { buildImageUrl } from "@/api/buildImageUrl";
import { useNavigate } from "react-router-dom";

interface Profesion {
    profesion_id: number;
    id_seccion_empresa: number;
    nombre_profesion: string;
    descripcion: string;
    imagen_url: string;
    codigo_acceso?: string | null;
    requiere_codigo?: number;
}

interface Seccion {
    id: number;
    nombreSeccion: string;
}

const EditarProfesiones: React.FC = () => {
    const [profesiones, setProfesiones] = useState<Profesion[]>([]);
    const [secciones, setSecciones] = useState<Seccion[]>([]);

    const [selectedProfesionId, setSelectedProfesionId] = useState<number | null>(null);
    const [nombreProfesion, setNombreProfesion] = useState("");
    const [descripcion, setDescripcion] = useState("");
    const [idSeccion, setIdSeccion] = useState<number | null>(null);
    const [foto, setFoto] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const navigate = useNavigate();
    const token = localStorage.getItem("token");

    const [codigoAcceso, setCodigoAcceso] = useState("");
    const [requiereCodigo, setRequiereCodigo] = useState(false);

    // Cargar profesiones y secciones
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [profRes, secRes] = await Promise.all([
                    safeFetch("/api/profesiones"),
                    safeFetch("/api/seccion")
                ]);
                if (!profRes.ok || !secRes.ok) throw new Error("Error cargando datos");

                setProfesiones(await profRes.json());
                setSecciones(await secRes.json());
            } catch (err: any) {
                setError(err.message);
            }
        };
        fetchData();
    }, []);

    // Cuando se selecciona una profesión, rellenar campos
    useEffect(() => {
        if (!selectedProfesionId) return;

        const p = profesiones.find((x) => x.profesion_id === selectedProfesionId);
        if (!p) return;

        setNombreProfesion(p.nombre_profesion);
        setDescripcion(p.descripcion);
        setIdSeccion(p.id_seccion_empresa);
        setPreview(buildImageUrl(p.imagen_url));
        setFoto(null);
        setCodigoAcceso(p.codigo_acceso || "");
        setRequiereCodigo(!!p.requiere_codigo);

      
        
        setError("");
        setSuccess("");
    }, [selectedProfesionId, profesiones]);

    // Cambiar imagen
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;

        // liberar preview anterior
        if (preview && preview.startsWith("blob:")) {
            URL.revokeObjectURL(preview);
        }

        setFoto(file);
        if (file) setPreview(URL.createObjectURL(file));
    };


    // Guardar cambios
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!selectedProfesionId || !nombreProfesion || !descripcion || !idSeccion) {
            setError("Completa todos los campos");
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("nombre_profesion", nombreProfesion);
            formData.append("descripcion", descripcion);
            formData.append("id_seccion_empresa", String(idSeccion));
            if (foto) formData.append("imagen", foto);
            formData.append("codigo_acceso", codigoAcceso);
            formData.append("requiere_codigo", requiereCodigo ? "1" : "0");

        

            const res = await safeFetch(`/api/editarProfesion/${selectedProfesionId}`, {

                method: "PUT", body: formData, headers: { Authorization: `Bearer ${token}` }
            }
            );

     


            if (!res.ok) throw new Error("Error al actualizar profesión");

            setSuccess("Profesión actualizada correctamente 🎉");
            if (foto) setPreview(URL.createObjectURL(foto));
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            const inputs = document.querySelectorAll<HTMLInputElement>('input[type="file"]');
            inputs.forEach(input => {
                input.value = "";
            });
        };
    }, []);

    useEffect(() => {
        return () => {
            if (preview && preview.startsWith("blob:")) {
                URL.revokeObjectURL(preview);
            }
        };
    }, [preview]);

    // si desmarcas checkbox → limpiar código
    useEffect(() => {
        if (!requiereCodigo) {
            setCodigoAcceso("");
        }
    }, [requiereCodigo]);


    return (
        <div className="w-full min-h-screen flex items-center justify-center bg-zinc-500 p-4 relative overflow-auto">
            {/* 🔙 Botón volver */}
            <div className="absolute top-4 left-4 z-10">
                <ButtonSiguiente
                    onClick={() => navigate("/backoffice")}
                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 shadow"
                >
                    ⬅ Volver
                </ButtonSiguiente>
            </div>

            <div className="w-full max-w-2xl dark:text-zinc-700 bg-zinc-400 p-6 rounded-xl shadow-md border-zinc-600 border-r-4 border-t-2 flex flex-col gap-6 overflow-auto shadow-yellow-600">
                <h2 className="text-2xl text-center font-bold">Editar Profesiones</h2>

                {error && <p className="text-red-600">{error}</p>}
                {success && <p className="text-green-600">{success}</p>}

                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                    <div className="flex flex-col md:flex-row gap-6">

                        {/* Columna izquierda */}
                        <div className="flex-1 w-full flex flex-col gap-4">
                            <div>
                                <label className="font-medium">Selecciona una profesión</label>
                                <Select
                                    value={selectedProfesionId ?? ""}
                                    onChange={(e) => setSelectedProfesionId(Number(e.target.value))}
                                    options={[
                                        { value: "", label: "Profesiones" }, // 👈 opción predeterminada

                                        ...[...profesiones]
                                            .sort((a, b) => a.nombre_profesion.localeCompare(b.nombre_profesion))
                                            .map((p) => ({
                                                value: p.profesion_id,
                                                label: p.nombre_profesion
                                            }))
                                    ]}
                                    className="w-full border p-2 rounded bg-white"
                                />


                            </div>

                            <div>
                                <label className="font-medium">Nombre de la profesión</label>
                                <Input
                                    value={nombreProfesion}
                                    onChange={(e) => setNombreProfesion(e.target.value)}
                                    className="w-full border p-2 rounded"
                                />
                            </div>

                            <div>
                                <label className="font-medium">Sección a la que pertenece</label>
                                <Select
                                    value={idSeccion ?? ""}
                                    onChange={(e) => setIdSeccion(Number(e.target.value))}
                                    options={secciones.map((s) => ({ value: s.id, label: s.nombreSeccion }))}
                                    className="w-full border p-2 rounded bg-white"
                                />
                            </div>

                            <div className="flex-1 flex flex-col">
                                <label className="font-medium">Descripción</label>
                                <textarea
                                    value={descripcion}
                                    onChange={(e) => setDescripcion(e.target.value)}
                                    className="w-full border p-2 rounded resize-none min-h-[120px] bg-white"
                                />
                            </div>

                            <div>
                                <label className="font-medium">Código de acceso</label>
                                <Input
                                    value={codigoAcceso}
                                    onChange={(e) => setCodigoAcceso(e.target.value)}
                                    className="w-full border p-2 rounded"
                                    placeholder="Opcional"
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={requiereCodigo}
                                    onChange={(e) => setRequiereCodigo(e.target.checked)}
                                    disabled={!requiereCodigo}
                                />
                                <label className="font-medium">Requiere código</label>
                            </div>

                        </div>

                        {/* Columna derecha */}
                        <div className="flex-1 w-full flex flex-col items-center gap-6">
                            <label className="font-medium">Imagen</label>

                            <label className="flex items-center justify-center py-2 w-3/6 bg-zinc-600 rounded-md border-white border text-white cursor-pointer transition hover:bg-gray-100 hover:text-zinc-800 hover:border-blue-700">
                                <span>Seleccionar Imagen</span>
                                <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                            </label>

                            {foto && <span className="text-sm truncate">{foto.name}</span>}

                            {preview && (
                                <img
                                    src={preview}
                                    className="w-full max-w-[192px] h-32 object-cover rounded-xl border-2 border-zinc-600"
                                />
                            )}
                        </div>
                    </div>

                    <ButtonSiguiente
                        type="submit"
                        disabled={loading}
                        className="bg-blue-600 text-white py-2 rounded hover:bg-blue-700 mt-4"
                    >
                        {loading ? "Guardando..." : "Actualizar Profesión"}
                    </ButtonSiguiente>
                </form>
            </div>
        </div>
    );


};

export default EditarProfesiones;
